import { GetSystemConfigPublicAPI, GetSystemConfigPrivateAPI } from '../api/API';
// Update endpoint removed - system config is now read-only

/**
 * SystemConfigService
 * 
 * Service for interacting with the System Configuration API.
 * This provides access to system-wide configuration data like:
 * - Product information
 * - Feature flags
 * - Default message templates
 * - System roles and permissions
 * - System settings
 */
class SystemConfigService {
  constructor() {
    this.config = null;
    this.publicConfig = null;
    this.isLoading = false;
    this.error = null;
  }

  /**
   * Fetch public system configuration from the API (no authentication required)
   * @returns {Promise} Promise that resolves with the public system configuration
   */
  async fetchPublicConfig(retryCount = 0) {
    const maxRetries = 1; // Reduzido de 3 para 1 — IABs não se beneficiam de muitos retries
    this.isLoading = true;
    this.error = null;

    try {
      return await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Request timeout'));
        }, 5000); // Reduzido de 10s para 5s — evita loading longo em IABs (Instagram, Facebook)
        
        GetSystemConfigPublicAPI((response) => {
          clearTimeout(timeoutId);
          this.isLoading = false;
          
          if (response && !response.error) {
            
            this.publicConfig = response;
            resolve(this.publicConfig);
          } else {
            
            this.error = response?.error || 'Failed to fetch public system configuration';
            reject(new Error(this.error));
          }
        });
      });
    } catch (error) {
      if (retryCount < maxRetries) {
        
        // Exponential backoff: 1s, 2s, 4s
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount)));
        return this.fetchPublicConfig(retryCount + 1);
      }
      
      
      throw error;
    } finally {
      if (retryCount == maxRetries) {
        this.isLoading = false;
      }
    }
  }

  /**
   * Fetch private system configuration from the API (requires authentication)
   * @returns {Promise} Promise that resolves with the system configuration
   */
  async fetchConfig(retryCount = 0) {
    const maxRetries = 3;
    this.isLoading = true;
    this.error = null;

    
    
    try {
      return await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Request timeout'));
        }, 10000); // 10 second timeout
        
        GetSystemConfigPrivateAPI((response) => {
          clearTimeout(timeoutId);
          this.isLoading = false;
          
          if (response && !response.error) {
            
            this.config = response;
            
            // Merge with public config if available
            if (this.publicConfig) {
              
              this.config = {
                ...this.publicConfig,
                ...this.config
              };
            }
            
            resolve(this.config);
          } else {
            
            this.error = response?.error || 'Failed to fetch system configuration';
            const error = new Error(this.error);
            error.code = response?.code;
            reject(error);
          }
        });
      });
    } catch (error) {
      // Sessão encerrada (API.js já tentou renovar): não adianta repetir
      if (error.code !== 'session_expired' && retryCount < maxRetries) {
        
        // Exponential backoff: 1s, 2s, 4s
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount)));
        return this.fetchConfig(retryCount + 1);
      }
      
      
      throw error;
    } finally {
      if (retryCount == maxRetries) {
        this.isLoading = false;
      }
    }
  }

  /**
   * Update system configuration - REMOVED
   * System configuration is now read-only and can only be modified by direct server-side changes.
   */

  /**
   * Check if a feature is enabled
   * @param {string} featureKey - The feature key to check
   * @returns {boolean} True if the feature is enabled, false otherwise
   */
  isFeatureEnabled(featureKey) {
    if (!this.config || !this.config.features) {
      
      return false;
    }
    
    return !!this.config.features[featureKey];
  }
  
  /**
   * Get public system configuration
   * @returns {Object|null} Public system configuration or null if not loaded
   */
  getPublicConfig() {
    return this.publicConfig;
  }

  /**
   * Get a system setting value
   * @param {string} settingKey - The setting key to get
   * @param {*} defaultValue - Default value to return if setting is not found
   * @returns {*} The setting value or defaultValue if not found
   */
  getSetting(settingKey, defaultValue = null) {
    if (!this.config || !this.config.settings) {
      
      return defaultValue;
    }
    
    return this.config.settings[settingKey] != undefined 
      ? this.config.settings[settingKey] 
      : defaultValue;
  }

  /**
   * Get default message template by key
   * @param {string} templateKey - The template key to get
   * @returns {string|null} The template content or null if not found
   */
  getDefaultMessageTemplate(templateKey) {
    if (!this.config || !this.config.defaultTemplates || !this.config.defaultTemplates.messages) {
      
      return null;
    }
    
    const template = this.config.defaultTemplates.messages.find(t => t.key == templateKey);
    return template ? template.content : null;
  }

  /**
   * Get product information
   * @returns {Object|null} Product information or null if not loaded
   */
  getProductInfo() {
    if (!this.config || !this.config.product) {
      
      return null;
    }
    
    return this.config.product;
  }

  /**
   * Get system roles and permissions
   * @returns {Array|null} Array of roles or null if not loaded
   */
  getRoles() {
    if (!this.config || !this.config.roles) {
      
      return null;
    }
    
    return this.config.roles;
  }
}

// Create singleton instance
const systemConfigService = new SystemConfigService();

export default systemConfigService;